import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { BookOpen, ArrowLeft, FileText, ChevronRight, ListOrdered, Clock, MapPin, Calendar, Users } from 'lucide-react';
import { userGroupService } from '../../services/userGroupService';
import { protocolService } from '../../services/protocolService';
import authService from '../../services/authService';
import ExecutionQuizRun from './ExecutionQuizRun';
import './ExecutionLearnTab.css';

const QUIZ_POOLS_COLLECTION = 'quiz_pools';
const QUIZ_ATTEMPTS_COLLECTION = 'quiz_attempts';

/** Derive quiz state per protocol from attempts: 'passed' | 'failed' | 'never' (never started). */
function buildQuizStateByProtocol(attempts) {
  const byProtocol = {};
  (attempts || []).forEach((a) => {
    const pid = a.protocolVersionId;
    if (!pid) return;
    if (a.passed === true) byProtocol[pid] = 'passed';
    else if (byProtocol[pid] !== 'passed' && (a.status === 'completed' || a.status === 'abandoned')) byProtocol[pid] = 'failed';
  });
  return byProtocol;
}

function isActiveProtocol(p) {
  const status = (p.status || '').toString().toLowerCase();
  if (status !== 'active') return false;
  return p.is_active !== false && p.isActive !== false;
}

/**
 * Learn tab: each active protocol = course. Each protocol with quiz generated = quiz inside.
 * Lists active protocols assigned to user's groups; click opens course (steps + Start quiz when available).
 */
export default function ExecutionLearnTab({ userId }) {
  const { t } = useTranslation('execution');
  const uid = authService.getCurrentUser()?.uid ?? userId;
  const [protocols, setProtocols] = useState([]);
  const [quizPoolByProtocol, setQuizPoolByProtocol] = useState({}); // protocolId -> pool (for first matching group)
  const [quizStateByProtocol, setQuizStateByProtocol] = useState({}); // protocolId -> 'passed' | 'failed' | 'never'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [quizRun, setQuizRun] = useState(null); // { poolId, passThresholdPercent } when set, show quiz run

  useEffect(() => {
    if (!uid) {
      setProtocols([]);
      setQuizPoolByProtocol({});
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [groupIdsRes, allProtocols] = await Promise.all([
          userGroupService.getGroupIdsForUser(uid),
          protocolService.getAllProtocols({}),
        ]);
        const groupIdsList = groupIdsRes || [];
        const active = (allProtocols || []).filter(isActiveProtocol);
        const assignedToMyGroups = groupIdsList.length === 0
          ? active
          : active.filter((p) => {
              const rules = p.assignmentRules || [];
              return rules.some((r) => r.assignedToGroup && groupIdsList.includes(r.assignedToGroup));
            });
        setProtocols(assignedToMyGroups);

        if (groupIdsList.length === 0) {
          setQuizPoolByProtocol({});
          setLoading(false);
          return;
        }
        const coll = collection(db, QUIZ_POOLS_COLLECTION);
        const chunkSize = 10;
        const pools = [];
        for (let i = 0; i < groupIdsList.length; i += chunkSize) {
          const chunk = groupIdsList.slice(i, i + chunkSize);
          const q = query(coll, where('userGroupId', 'in', chunk));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            const data = d.data();
            if (data.generationStatus === 'success') {
              pools.push({ id: d.id, ...data });
            }
          });
        }
        const byProtocol = {};
        pools.forEach((pool) => {
          const pid = pool.protocolVersionId || pool.id?.split('_')[0];
          if (pid && !byProtocol[pid]) byProtocol[pid] = pool;
        });
        setQuizPoolByProtocol(byProtocol);

        const attemptsRef = collection(db, QUIZ_ATTEMPTS_COLLECTION);
        const attemptsQuery = query(attemptsRef, where('staffId', '==', uid));
        const attemptsSnap = await getDocs(attemptsQuery);
        const attempts = attemptsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setQuizStateByProtocol(buildQuizStateByProtocol(attempts));
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('ExecutionLearnTab load error', e);
        }
        setError(e.message || 'Failed to load courses');
        setProtocols([]);
        setQuizPoolByProtocol({});
        setQuizStateByProtocol({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [uid]);

  const refetchQuizState = async () => {
    if (!uid) return;
    try {
      const q = query(collection(db, QUIZ_ATTEMPTS_COLLECTION), where('staffId', '==', uid));
      const snap = await getDocs(q);
      const attempts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setQuizStateByProtocol(buildQuizStateByProtocol(attempts));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('Refetch quiz state failed', e);
    }
  };

  const handleStartQuiz = (pool, protocol) => {
    if (pool?.id) {
      const pct = protocol?.learningAssessment?.passThresholdPercent ?? 70;
      setQuizRun({ poolId: pool.id, passThresholdPercent: pct });
    }
  };

  if (quizRun) {
    return (
      <ExecutionQuizRun
        poolId={quizRun.poolId}
        passThresholdPercent={quizRun.passThresholdPercent}
        onBack={() => {
          setQuizRun(null);
          refetchQuizState();
        }}
      />
    );
  }

  if (selectedProtocol) {
    const pool = quizPoolByProtocol[selectedProtocol.id || selectedProtocol.firestoreId];
    const hasQuiz = Boolean(pool?.id);
    return (
      <ExecutionCourseDetail
        protocol={selectedProtocol}
        hasQuiz={hasQuiz}
        pool={pool}
        onBack={() => setSelectedProtocol(null)}
        onStartQuiz={() => handleStartQuiz(pool, selectedProtocol)}
      />
    );
  }

  if (!uid) return null;

  if (loading) {
    return (
      <div className="execution-learn-tab">
        <h2>{t('trainingMaterials')}</h2>
        <p className="execution-learn-loading">{t('tasks:loadingTasks', 'Se încarcă...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="execution-learn-tab">
        <h2>{t('trainingMaterials')}</h2>
        <p className="execution-learn-error">{error}</p>
      </div>
    );
  }

  if (protocols.length === 0) {
    return (
      <div className="execution-learn-tab">
        <h2>{t('trainingMaterials')}</h2>
        <div className="execution-learn-placeholder">
          <BookOpen size={48} color="#94a3b8" />
          <p>{t('learnComingSoon', 'Cursuri și quiz-uri vor fi disponibile aici.')}</p>
        </div>
      </div>
    );
  }

  const protocolId = (p) => p.id || p.firestoreId;
  return (
    <div className="execution-learn-tab">
      <h2>{t('trainingMaterials')}</h2>
      <ul className="execution-learn-list">
        {protocols.map((p) => {
          const pid = protocolId(p);
          const hasQuiz = Boolean(quizPoolByProtocol[pid]);
          const quizState = quizStateByProtocol[pid] || 'never'; // 'passed' | 'failed' | 'never'
          return (
            <li key={pid}>
              <button
                type="button"
                className="execution-learn-card"
                onClick={() => setSelectedProtocol(p)}
                aria-label={p.name}
              >
                <FileText size={24} className="execution-learn-card-icon" />
                <div className="execution-learn-card-body">
                  <strong>{p.name}</strong>
                  <span className="execution-learn-card-meta">
                    {p.steps?.length ?? 0} {t('learnSteps', 'pași')}
                    {hasQuiz && (
                      <span className={`execution-learn-badge execution-learn-badge-test execution-learn-badge-test--${quizState}`}>
                        {t('learnTestBadge', 'Test')}
                      </span>
                    )}
                  </span>
                </div>
                <ChevronRight size={20} className="execution-learn-card-arrow" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatActionEquipment(equipment) {
  if (!equipment || !Array.isArray(equipment)) return null;
  return equipment.map((e) => (typeof e === 'object' ? e.name : e)).filter(Boolean).join(', ');
}
function formatActionSupplies(supplies) {
  if (!supplies || !Array.isArray(supplies)) return null;
  return supplies.map((s) => (typeof s === 'object' ? s.name : s)).filter(Boolean).join(', ');
}

/**
 * Course detail: protocol steps with full details (description, actions with description/duration/equipment/supplies/verification) + "Start quiz" fixed above footer.
 */
function ExecutionCourseDetail({ protocol, hasQuiz, pool, onBack, onStartQuiz }) {
  const { t } = useTranslation('execution');
  const steps = (protocol.steps || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const protocolCode = protocol.protocol_code || protocol.protocolCode || protocol.id || protocol.firestoreId;

  return (
    <div className="execution-learn-tab execution-course-detail">
      <div className="execution-course-detail-scroll">
        <header className="execution-course-detail-header">
          <button type="button" className="execution-course-detail-back" onClick={onBack}>
            <ArrowLeft size={20} />
            {t('trainingMaterials')}
          </button>
        </header>
        <div className="execution-course-detail-card">
          {protocolCode && (
            <span className="execution-course-detail-id">{protocolCode}</span>
          )}
          <h1 className="execution-course-detail-title">{protocol.name}</h1>
          <span className="execution-course-detail-steps">
            {steps.length} {t('learnSteps', 'pași')}
          </span>
        </div>
        <section className="execution-course-detail-section">
          <h2 className="execution-course-detail-section-title">
            <FileText size={20} />
            {t('learnAboutProtocol', 'Despre protocol')}
          </h2>
          <div className="execution-course-detail-about-card">
            {protocol.description && (
              <p className="execution-course-detail-description">{protocol.description}</p>
            )}
            {(protocol.version || protocolCode || protocol.defaultTaskTemplateName || protocol.category) && (
              <div className="execution-course-detail-meta">
                {[protocol.version && `${t('learnVersion', 'Versiune')} ${protocol.version}`, protocolCode && (protocol.protocol_code || protocolCode), protocol.defaultTaskTemplateName && `${t('learnTemplate', 'Șablon')}: ${protocol.defaultTaskTemplateName}`, protocol.category && `${t('learnCategory', 'Categorie')}: ${protocol.category}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
        </section>
        {protocol.applicabilityRules && protocol.applicabilityRules.length > 0 && (
          <section className="execution-course-detail-section">
            <h2 className="execution-course-detail-section-title">
              <MapPin size={20} />
              {t('learnWhenWhere')}
            </h2>
            <div className="execution-course-detail-info-cards">
              {protocol.applicabilityRules.map((rule, idx) => (
                <div key={idx} className="execution-course-detail-info-card">
                  <span className="execution-course-detail-info-label">
                    {t(`structureType_${rule.structureType}`, rule.structureType)}
                  </span>
                  <span className="execution-course-detail-info-value">
                    {rule.selectedEntityNames?.join(', ') || rule.selectedEntities?.join(', ') || (rule.includeAll ? t('learnAll') : t('learnNotSpecified'))}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
        {(protocol.schedulingRules && protocol.schedulingRules.length > 0) && (
          <section className="execution-course-detail-section">
            <h2 className="execution-course-detail-section-title">
              <Calendar size={20} />
              {t('learnWhen')}
            </h2>
            <div className="execution-course-detail-info-cards">
              {protocol.schedulingRules.map((r, idx) => {
                const freqNorm = (r.frequency || '').toLowerCase().replace(/-/g, '_');
                const freqKey = freqNorm ? `learnSchedule_${freqNorm}` : 'learnNoSchedule';
                const freqLabel = t(freqKey, r.frequency || t('learnNoSchedule'));
                const detailParts = [];
                if (r.recurrenceType) {
                  const recKey = `learnSchedule_${r.recurrenceType.toLowerCase()}`;
                  if (t(recKey) !== recKey) detailParts.push(t(recKey));
                }
                if (r.times && r.times.length > 0) {
                  const timeStr = r.times.slice(0, 3).map(({ hour = 0, minute = 0 }) => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`).join(', ');
                  if (r.times.length > 3) detailParts.push(`${timeStr}…`);
                  else detailParts.push(timeStr);
                }
                const detail = detailParts.length > 0 ? ` · ${detailParts.join(' ')}` : '';
                return (
                  <div key={idx} className="execution-course-detail-info-card">
                    <span className="execution-course-detail-info-value execution-course-detail-info-value--full">
                      {freqLabel}{detail}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {(protocol.assignmentRules && protocol.assignmentRules.length > 0) && (
          <section className="execution-course-detail-section">
            <h2 className="execution-course-detail-section-title">
              <Users size={20} />
              {t('learnWho')}
            </h2>
            <div className="execution-course-detail-info-cards">
              {protocol.assignmentRules.map((r, idx) => {
                const items = [];
                if (r.assignedToGroupName) items.push({ label: t('learnWhoGroup'), name: r.assignedToGroupName });
                if (r.assignedToUserName) items.push({ label: t('learnWhoUser'), name: r.assignedToUserName });
                if (items.length === 0) items.push({ label: null, name: t('learnNotSpecified') });
                return (
                  <div key={idx} className="execution-course-detail-info-card execution-course-detail-info-card--who">
                    {items.map((item, i) => (
                      <div key={i} className="execution-course-detail-info-row">
                        {item.label && <span className="execution-course-detail-info-label">{item.label}:</span>}
                        <span className="execution-course-detail-info-value">{item.name}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </section>
        )}
        <section className="execution-course-detail-section">
          <h2 className="execution-course-detail-section-title">
            <ListOrdered size={20} />
            {t('learnProtocolSteps', 'Pași protocol')}
          </h2>
          {steps.length > 0 ? (
            <ol className="execution-course-detail-steps-list">
              {steps.map((step, stepIdx) => {
                const actions = (step.actions || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
                return (
                  <li key={step.id || stepIdx} className="execution-course-detail-step">
                    <div className="execution-course-detail-step-header">
                      <span className="execution-course-detail-step-num">{step.order ?? stepIdx + 1}</span>
                      <div className="execution-course-detail-step-head">
                        <strong>{step.name}</strong>
                        <div className="execution-course-detail-step-meta">
                          {step.estimatedDuration != null && (
                            <span className="execution-course-detail-step-duration">
                              <Clock size={14} /> {step.estimatedDuration} min
                            </span>
                          )}
                          {step.isRequired !== undefined && (
                            <span className="execution-course-detail-step-required">
                              {step.isRequired ? t('learnRequired', 'Obligatoriu') : t('learnOptional', 'Opțional')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {step.description && (
                      <p className="execution-course-detail-step-desc">{step.description}</p>
                    )}
                    {actions.length > 0 && (
                      <div className="execution-course-detail-actions">
                        <ul className="execution-course-detail-actions-list">
                          {actions.map((action, actionIdx) => (
                            <li key={action.id || actionIdx} className="execution-course-detail-action">
                              <span className="execution-course-detail-action-name">
                                {action.order ?? actionIdx + 1}. {action.name}
                              </span>
                              {action.estimatedDuration != null && (
                                <div className="execution-course-detail-action-duration">
                                  <Clock size={14} /> {action.estimatedDuration} min
                                </div>
                              )}
                              {(action.description || action.technique?.description) && (
                                <p className="execution-course-detail-action-desc">
                                  {action.description || action.technique?.description}
                                </p>
                              )}
                              <div className="execution-course-detail-action-meta">
                                {formatActionEquipment(action.equipment || action.requiredEquipment) && (
                                  <span>{t('learnEquipment', 'Echipament')}: {formatActionEquipment(action.equipment || action.requiredEquipment)}</span>
                                )}
                                {formatActionSupplies(action.supplies || action.requiredSupplies) && (
                                  <span>{t('learnSupplies', 'Materiale')}: {formatActionSupplies(action.supplies || action.requiredSupplies)}</span>
                                )}
                                {(action.verificationMethod || action.technique?.verificationMethod) && (
                                  <span>{t('learnVerification', 'Verificare')}: {action.verificationMethod || action.technique?.verificationMethod}</span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="execution-course-detail-empty">{t('learnNoSteps', 'Niciun pas definit.')}</p>
          )}
        </section>
        {/* Start test button in flow so it's always visible */}
        {hasQuiz && (
          <div className="execution-course-detail-quiz-inline">
            <button
              type="button"
              className="execution-course-detail-quiz-btn"
              onClick={onStartQuiz}
            >
              {t('learnStartTest', 'Începe testul')} →
            </button>
          </div>
        )}
        <div className="execution-course-detail-back-footer">
          <button type="button" className="execution-course-detail-back execution-course-detail-back--footer" onClick={onBack}>
            <ArrowLeft size={20} />
            {t('learnBackToTraining', 'Înapoi la Materiale de formare')}
          </button>
        </div>
        {hasQuiz && <div className="execution-course-detail-quiz-spacer" aria-hidden />}
      </div>
      {hasQuiz && (
        <div className="execution-course-detail-quiz-bar">
          <button
            type="button"
            className="execution-course-detail-quiz-btn"
            onClick={onStartQuiz}
          >
            {t('learnStartTest', 'Începe testul')} →
          </button>
        </div>
      )}
    </div>
  );
}
